import React, { useState, useEffect } from 'react';
import { MoonIcon, SunIcon, ArrowIcon } from '../icons/WeatherIcons';

const HourlyTemperatureCard = ({ weatherData }) => {
  const [currentPage, setCurrentPage] = useState(0);
  const [hoursPerPage, setHoursPerPage] = useState(6);

  useEffect(() => {
    const updateHoursPerPage = () => {
      const newHoursPerPage = window.innerWidth < 768 ? 4 : 6;
      setHoursPerPage(newHoursPerPage);
      // Reset to first page when hours per page changes
      setCurrentPage(0);
    };

    updateHoursPerPage();
    window.addEventListener('resize', updateHoursPerPage);
    return () => window.removeEventListener('resize', updateHoursPerPage);
  }, []);

  if (!weatherData.hourlyTemperature || !weatherData.hourlyTime || weatherData.hourlyTemperature.length === 0) {
    return (
      <div className="bg-white/90 backdrop-blur-sm rounded-2xl p-6 xl:col-span-2 min-h-[280px]">
        <p>No hourly data available</p>
      </div>
    );
  }

  const hourlyData = weatherData.hourlyTemperature.slice(0, 24);
  const hourlyTimes = weatherData.hourlyTime.slice(0, 24);

  const totalPages = Math.ceil(hourlyData.length / hoursPerPage);
  const startIndex = currentPage * hoursPerPage;
  const endIndex = startIndex + hoursPerPage;
  const currentHours = hourlyData.slice(startIndex, endIndex);
  const currentTimes = hourlyTimes.slice(startIndex, endIndex);

  const formatHour = (timeString) => {
    const date = new Date(timeString);
    return date.toLocaleTimeString('en-US', {
      hour: 'numeric',
      minute: '2-digit',
      timeZone: weatherData.timezoneFull
    });
  };

  // Keep icons stable across °C/°F by NOT tying them to temperature thresholds.
  // Use time-of-day only.
  const getWeatherIcon = (_temp, timeString) => {
    const h = new Date(timeString).getHours();
    return h >= 6 && h < 18 ? 'sun' : 'moon';
  };

  const handlePrevious = () => {
    setCurrentPage((prev) => Math.max(0, prev - 1));
  };

  const handleNext = () => {
    setCurrentPage((prev) => Math.min(totalPages - 1, prev + 1));
  };

  return (
    <div className="bg-white backdrop-blur-sm p-6 rounded-2xl">
      <div className="flex items-center justify-between mb-10">
        <h4 className="text-xl font-bold text-gray-800 m-0">Hourly Temperature</h4>
        <div className="flex items-center gap-2">
          <button
            onClick={handlePrevious}
            disabled={currentPage === 0}
            className="px-3 py-1 rounded-lg bg-[#3c6ac8] hover:[#1f4a73] disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            aria-label="Previous hours"
          >
            <ArrowIcon className="w-5 h-5 rotate-90 text-white" />
          </button>
          <span className="text-md font-bold text-gray-800">
            {currentPage + 1} / {totalPages}
          </span>
          <button
            onClick={handleNext}
            disabled={currentPage === totalPages - 1}
            className="px-3 py-1 rounded-lg bg-[#3c6ac8] hover:[#1f4a73] disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            aria-label="Next hours"
          >
            <ArrowIcon className="w-5 h-5 -rotate-90 text-white" />
          </button>
        </div>
        
      </div>
      <div className="flex gap-4 justify-between">
        {currentHours.map((temp, index) => (
          <div 
            key={startIndex + index} 
            className="flex flex-col items-center gap-2 flex-1 py-2"
          >
            <div className="text-md text-gray-600 font-medium">
              {formatHour(currentTimes[index])}
            </div>
            <div className="text-2xl">
              {getWeatherIcon(temp, currentTimes[index]) === 'sun' ? (
                <SunIcon className="w-7 h-7 text-gray-800" />
              ) : (
                <MoonIcon className="w-7 h-7 text-gray-800" />
              )}
            </div>
            <div className="text-base font-semibold text-gray-800">
              {Math.round(temp)}°{weatherData.temperatureUnit === 'fahrenheit' ? 'F' : 'C'}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default HourlyTemperatureCard;

